'use client';

import { getResourceData } from '@/api/public';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { useCallback, useEffect, useRef, useState } from 'react';
import FlashSaleAreaMobile from './FlashSaleArea.m';
import FlashSaleAreaPc from './FlashSaleArea.p';

interface FlashSaleAreaProps {
  listData: any[];
  flashSaleData: {
    title?: string;
    description?: string;
    left_image?: string;
    right_image?: string;
    sale_image?: string;
    price_word?: string;
    time_word?: string;
  };
  remainingSeconds: number;
  restrictPurchase: number; // 1: 限购, 2: 不限购
  onRestrictPurchaseChange?: (restrictPurchase: number) => void; // 限购状态变化回调，通知父组件
}

export default function FlashSaleArea({
  listData: initialListData,
  flashSaleData,
  remainingSeconds: initialRemainingSeconds,
  restrictPurchase: initialRestrictPurchase,
  onRestrictPurchaseChange,
}: FlashSaleAreaProps) {
  // 组件内部状态管理
  const [listData, setListData] = useState(initialListData); // 商品列表数据
  const [remainingSeconds, setRemainingSeconds] = useState(initialRemainingSeconds);
  const [restrictPurchase, setRestrictPurchase] = useState(initialRestrictPurchase);
  const [isCountdownVisible, setIsCountdownVisible] = useState(true); // 控制倒计时显示/隐藏

  // 获取token状态
  const token = useStore((state) => state.token);
  const clientTokenReady = useStore((state) => state.clientTokenReady);
  const prevTokenRef = useRef<string | undefined>(undefined); // 用于追踪token变化
  const isRefreshingRef = useRef(false); // 使用ref避免闭包问题

  // 刷新Flash Sale数据
  const refreshFlashSaleData = useCallback(async () => {
    if (isRefreshingRef.current) {
      logger.log('正在刷新中，跳过本次请求');
      return;
    }

    isRefreshingRef.current = true;
    setIsCountdownVisible(false); // 开始刷新时隐藏倒计时
    try {
      logger.log('开始刷新Flash Sale数据...');

      const response = await getResourceData({
        special_page: {
          key: 'BlackFridayFlashSales',
        },
      });

      if (response?.data?.special_page) {
        const newEndTime = response.data.special_page.end_time || 0;

        // 检查黑五活动是否结束
        if (newEndTime && Date.now() / 1000 > newEndTime) {
          logger.log('黑五活动已结束，跳转到黑五专题页');
          window.location.href = '/campaigns/spring-sale';
          return;
        }

        // 更新组件内部状态（包括倒计时、限购状态和商品列表）
        const newRemainingSeconds = response?.data?.special_page.remaining_seconds || 0;
        const newRestrictPurchase = response?.data?.special_page.restrict_purchase || 2;
        const newListData = response?.data?.special_page?.product_list?.list || [];

        setRemainingSeconds(newRemainingSeconds);
        setRestrictPurchase((prevRestrictPurchase) => {
          // 通知父组件限购状态变化（用于Promotion促销区域）
          if (onRestrictPurchaseChange && newRestrictPurchase !== prevRestrictPurchase) {
            onRestrictPurchaseChange(newRestrictPurchase);
          }
          return newRestrictPurchase;
        });
        setListData(newListData); // 更新商品列表

        logger.log('Flash Sale数据已刷新:', {
          remainingSeconds: newRemainingSeconds,
          restrictPurchase: newRestrictPurchase,
          listDataCount: newListData.length,
          endTime: newEndTime,
        });
      }
    } catch (error) {
      logger.error('刷新Flash Sale数据失败:', error);
    } finally {
      setIsCountdownVisible(true); // 刷新完成后显示倒计时
      isRefreshingRef.current = false;
    }
  }, [onRestrictPurchaseChange]);

  // 倒计时结束处理
  const handleCountdownEnd = useCallback(() => {
    logger.log('Flash Sale倒计时结束，开始刷新数据...');
    // refreshFlashSaleData();
  }, [refreshFlashSaleData]);

  useEffect(() => {
    logger.log('remainingSeconds', remainingSeconds);
    refreshFlashSaleData();
  }, []);

  // 监听token变化，当token发生变化时刷新商品数据
  useEffect(() => {
    logger.log('token监听触发，当前token:', token, '之前的token:', prevTokenRef.current);

    // 首次初始化，记录当前token，但不触发刷新
    if (prevTokenRef.current === undefined) {
      prevTokenRef.current = token;
      logger.log('首次初始化token，不触发刷新');
      return;
    }

    // 如果token发生变化，则刷新数据
    if (prevTokenRef.current !== token) {
      logger.log('检测到token变化，准备刷新Flash Sale商品数据:', {
        prevToken: prevTokenRef.current,
        newToken: token,
      });
      prevTokenRef.current = token;

      // 使用setTimeout确保状态更新完成后再刷新
      setTimeout(() => {
        refreshFlashSaleData();
      }, 100);
    }
  }, [token, clientTokenReady, refreshFlashSaleData]);

  // 如果没有商品数据或者限购状态不是2，不显示Flash Sale区域
  if (!listData || listData.length === 0 || restrictPurchase !== 2) {
    return null;
  }

  return (
    <>
      <WebOnly>
        <FlashSaleAreaPc
          listData={listData}
          flashSaleData={flashSaleData}
          remainingSeconds={remainingSeconds}
          isCountdownVisible={isCountdownVisible}
          onCountdownEnd={handleCountdownEnd}
        />
      </WebOnly>
      <WapOnly>
        <FlashSaleAreaMobile
          listData={listData}
          flashSaleData={flashSaleData}
          remainingSeconds={remainingSeconds}
          isCountdownVisible={isCountdownVisible}
          onCountdownEnd={handleCountdownEnd}
        />
      </WapOnly>
    </>
  );
}
